import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, User, Building, MapPin, Phone, Info, ChevronUp, ChevronDown, Filter } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import toast from 'react-hot-toast';

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [provinceFilter, setProvinceFilter] = useState('');
  const [registrationTypeFilter, setRegistrationTypeFilter] = useState('');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState(''); // registered/unregistered
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Dynamic filter values from backend
  const [filterValues, setFilterValues] = useState({
    provinces: [],
    registration_types: [],
    registration_counts: { registered: 0, unregistered: 0 }
  });

  // Province list from database (static - will never change)
  const provinces = [
    'AZAD JAMMU AND KASHMIR',
    'BALOCHISTAN',
    'CAPITAL TERRITORY',
    'GILGIT BALTISTAN',
    'KHYBER PAKHTUNKHWA',
    'PUNJAB',
    'SINDH'
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    province: '',
    cnic: '',
    ntn: '',
    buyer_strn: '',
    ntn_cnic: '', // Legacy field for backward compatibility
    // FBR-specific fields
    buyer_registration_type: 'Individual',
    buyer_business_name: '',
    // FBR Status fields
    fbr_registration_status: 'unknown',
    fbr_active_status: 'unknown'
  });

  const [formErrors, setFormErrors] = useState({
    cnic: '',
    ntn: ''
  });

  // FBR Status checking state
  const [fbrStatusLoading, setFbrStatusLoading] = useState(false);
  const [fbrStatusOptions, setFbrStatusOptions] = useState({
    registration_status_options: [],
    active_status_options: []
  });

  useEffect(() => {
    fetchFilterValues();
  }, []);

  useEffect(() => {
    fetchClients();
    fetchFbrStatusOptions();
    setSelectedClients([]);
    setSelectAll(false);
  }, [currentPage, searchTerm, provinceFilter, registrationTypeFilter, registrationStatusFilter, dateFrom, dateTo, sortField, sortDirection, itemsPerPage]);

  // Validation functions
  const validateCNIC = (cnic) => {
    if (!cnic) return '';
    const cleanCNIC = cnic.replace(/[^0-9]/g, '');
    if (cleanCNIC.length !== 13) {
      return 'CNIC must be exactly 13 digits';
    }
    return '';
  };

  const validateNTN = (ntn) => {
    if (!ntn) return '';
    const trimmed = ntn.trim();
    if (!/^[A-Za-z0-9]{7,8}$/.test(trimmed)) {
      return 'NTN must be 7 or 8 characters (alphanumeric)';
    }
    return '';
  };

  // FBR Status checking functions
  const fetchFbrStatusOptions = async () => {
    try {
      const response = await apiGet('/api/clients/fbr-status-options');
      if (response.ok) {
        setFbrStatusOptions(response.data);
      } else {
        console.error('Error fetching FBR status options:', response.data);
        toast.error('Failed to fetch FBR status options');
      }
    } catch (error) {
      console.error('Error fetching FBR status options:', error);
      toast.error('Failed to fetch FBR status options');
    }
  };

  const checkFbrStatus = async (cnic, ntn) => {
    if (!cnic && !ntn) return;
    
    // Validate CNIC format
    if (cnic) {
      const cleanCNIC = cnic.replace(/[^0-9]/g, '');
      if (cleanCNIC.length !== 13) return;
    }
    
    // Validate NTN format (7 or 8 alphanumeric)
    if (ntn) {
      const trimmed = ntn.trim();
      if (!/^[A-Za-z0-9]{7,8}$/.test(trimmed)) return;
    }
    
    setFbrStatusLoading(true);
    try {
      const response = await apiPost('/api/clients/check-fbr-status', {
        cnic: cnic || null,
        ntn: ntn || null
      });
      
      if (response.ok) {
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          fbr_registration_status: data.registration_status || 'unknown',
          fbr_active_status: data.active_status || 'unknown'
        }));
        
        if (data.success) {
          toast.success('FBR status updated successfully');
        } else {
          toast.error('FBR status check failed - using manual selection');
        }
      }
    } catch (error) {
      console.error('Error checking FBR status:', error);
      toast.error('FBR status check failed - using manual selection');
    } finally {
      setFbrStatusLoading(false);
    }
  };

  // Debounced FBR status checking
  const debouncedCheckFbrStatus = (() => {
    let timeoutId;
    return (cnic, ntn) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        checkFbrStatus(cnic, ntn);
      }, 1000); // Wait 1 second after user stops typing
    };
  })();

  const handleInputChange = (field, value) => {
    setFormData({...formData, [field]: value});
    
    // Clear previous errors
    setFormErrors({...formErrors, [field]: ''});
    
    // Validate CNIC
    if (field === 'cnic') {
      const error = validateCNIC(value);
      setFormErrors({...formErrors, cnic: error});
      
      // Trigger FBR status check for CNIC
      if (!error && value) {
        debouncedCheckFbrStatus(value, formData.ntn);
      }
    }
    
    // Validate NTN
    if (field === 'ntn') {
      const error = validateNTN(value);
      setFormErrors({...formErrors, ntn: error});
      
      // Trigger FBR status check for NTN
      if (!error && value) {
        debouncedCheckFbrStatus(formData.cnic, value);
      }
    }
  };

  const fetchFilterValues = async () => {
    try {
      const response = await apiGet('/api/clients/filter-values');
      if (response.ok) {
        setFilterValues(response.data);
      }
    } catch (error) {
      console.error('Error fetching filter values:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (provinceFilter) {
        params.province = provinceFilter;
      }
      if (registrationTypeFilter) {
        params.registration_type = registrationTypeFilter;
      }
      if (registrationStatusFilter) {
        params.registration_status = registrationStatusFilter;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await apiGet('/api/clients/', params);

      if (response.ok) {
        setClients(response.data.clients || []);
        setTotalClients(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
      } else {
        console.error('Failed to fetch clients:', response.data);
        setClients([]);
        toast.error('Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      toast.error('Error fetching clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === 'asc' ?
      <ChevronUp className="h-4 w-4 text-amber-600" /> :
      <ChevronDown className="h-4 w-4 text-amber-600" />;
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setProvinceFilter('');
    setRegistrationTypeFilter('');
    setRegistrationStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    const cnicError = validateCNIC(formData.cnic);
    const ntnError = validateNTN(formData.ntn);
    
    if (cnicError || ntnError) {
      setFormErrors({
        cnic: cnicError,
        ntn: ntnError
      });
      toast.error('Please fix validation errors before submitting');
      return;
    }
    
    try {
      // Convert string values to proper types for backend
      const clientData = {
        name: formData.name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        province: formData.province || undefined,
        cnic: formData.cnic || undefined,
        ntn: formData.ntn || undefined,
        buyer_strn: formData.buyer_strn || undefined,
        ntn_cnic: formData.ntn_cnic || undefined, // Legacy field for backward compatibility
        // FBR-specific fields
        buyer_registration_type: formData.buyer_registration_type,
        buyer_business_name: formData.buyer_business_name || undefined,
        // FBR Status fields
        fbr_registration_status: formData.fbr_registration_status,
        fbr_active_status: formData.fbr_active_status
      };

      let response;
      if (editingClient) {
        response = await apiPut(`/api/clients/${editingClient.id}`, clientData);
      } else {
        response = await apiPost('/api/clients/', clientData);
      }

      if (response.ok) {
        toast.success(editingClient ? 'Client updated successfully!' : 'Client created successfully!');
        setShowModal(false);
        setEditingClient(null);
        resetForm();
        fetchClients();
      } else {
        const errorMessage = response.data?.detail || response.data?.message || 'Error saving client';
        
        // Check if it's a CNIC duplicate error
        if (errorMessage.includes('CNIC') && errorMessage.includes('already exists')) {
          setFormErrors({
            cnic: 'This CNIC number is already registered for another client',
            ntn: ''
          });
          toast.error('CNIC number already exists. Please use a different CNIC.');
        }
        // Check if it's an NTN duplicate error
        else if (errorMessage.includes('NTN') && errorMessage.includes('already exists')) {
          setFormErrors({
            cnic: '',
            ntn: 'This NTN number is already registered for another client'
          });
          toast.error('NTN number already exists. Please use a different NTN.');
        }
        // Check if it's an email duplicate error
        else if (errorMessage.includes('email') && errorMessage.includes('already exists')) {
          toast.error('Email already exists. Please use a different email address.');
        } 
        else {
          toast.error(`Error: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Error saving client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      province: client.province || '',
      cnic: client.cnic || '',
      ntn: client.ntn || '',
      buyer_strn: client.buyer_strn || '',
      ntn_cnic: client.ntn_cnic || '', // Legacy field for backward compatibility
      // FBR-specific fields
      buyer_registration_type: client.buyer_registration_type || 'Individual',
      buyer_business_name: client.buyer_business_name || '',
      // FBR Status fields
      fbr_registration_status: client.fbr_registration_status || 'unknown',
      fbr_active_status: client.fbr_active_status || 'unknown'
    });
    setFormErrors({ cnic: '', ntn: '' }); // Clear any previous errors
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to deactivate this client? It will be hidden from the clients list but can be reactivated later.')) return;

    try {
      const response = await apiDelete(`/api/clients/${clientId}`);

      if (response.ok) {
        toast.success('Client deactivated successfully! It has been hidden from the clients list.');
        fetchClients();
      } else {
        const errorMessage = response.data?.detail || response.data?.message || 'Failed to deactivate client';
        toast.error(`Failed to deactivate client: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error deactivating client:', error);
      toast.error('Error deactivating client');
    }
  };

  const handleSelectClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
      setSelectAll(false);
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(client => client.id));
    }
    setSelectAll(!selectAll);
  };

  const bulkDeactivateClients = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select clients to deactivate');
      return;
    }
    if (!window.confirm(`Are you sure you want to deactivate ${selectedClients.length} client(s)?`)) return;
    
    try {
      const response = await apiPost('/api/clients/bulk-deactivate', selectedClients);
      if (response.ok) {
        toast.success(response.data.message || `${selectedClients.length} client(s) deactivated successfully!`);
        setSelectedClients([]);
        setSelectAll(false);
        fetchClients();
      } else {
        toast.error(response.data.detail || 'Error deactivating clients');
      }
    } catch (error) {
      toast.error('Error deactivating clients');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      province: '',
      cnic: '',
      ntn: '',
      buyer_strn: '',
      ntn_cnic: '', // Legacy field for backward compatibility
      // FBR-specific fields
      buyer_registration_type: 'Individual',
      buyer_business_name: '',
      // FBR Status fields
      fbr_registration_status: 'unknown',
      fbr_active_status: 'unknown'
    });
    setFormErrors({ cnic: '', ntn: '' }); // Clear validation errors
  };



  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderClientModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-slate-900 mb-4">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Province
                  </label>
                  <select
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Select Province</option>
                    {provinces.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* CNIC and NTN Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    CNIC Number
                  </label>
                  <input
                    type="text"
                    value={formData.cnic}
                    onChange={(e) => handleInputChange('cnic', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.cnic 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-slate-300 focus:ring-amber-500'
                    }`}
                    placeholder="1234567890123"
                    maxLength="13"
                  />
                  {formErrors.cnic && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.cnic}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">Exactly 13 digits</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    NTN Number
                  </label>
                  <input
                    type="text"
                    value={formData.ntn}
                    onChange={(e) => handleInputChange('ntn', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.ntn 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-slate-300 focus:ring-amber-500'
                    }`}
                    placeholder="9999997 or ABC12XY"
                    maxLength="8"
                  />
                  {formErrors.ntn && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.ntn}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">7 or 8 characters (alphanumeric)</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  STRN
                </label>
                <input
                  type="text"
                  value={formData.buyer_strn}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      buyer_strn: e.target.value.slice(0, 25),
                    })
                  }
                  maxLength={25}
                  placeholder="Optional identifier"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="mt-1 text-xs text-slate-500">Max 25 characters (optional)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Registration Type
                  </label>
                  <select
                    value={formData.buyer_registration_type}
                    onChange={(e) => setFormData({...formData, buyer_registration_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="Individual">Individual</option>
                    <option value="Registered">Registered Business</option>
                    <option value="Unregistered">Unregistered Business</option>
                  </select>
                </div>
              </div>

              {/* FBR Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center mb-4">
                  <Info className="h-5 w-5 text-amber-600 mr-2" />
                  <h4 className="text-md font-medium text-slate-900">FBR Configuration</h4>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  These fields are required for FBR (Federal Board of Revenue) compliance and invoice submission.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Business Name (for Business Clients)
                  </label>
                  <input
                    type="text"
                    value={formData.buyer_business_name}
                    onChange={(e) => setFormData({...formData, buyer_business_name: e.target.value})}
                    placeholder="Enter business name if client is a business entity"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Required if registration type is "Registered" or "Unregistered" business
                  </p>
                </div>

                {/* FBR Status Dropdowns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      FBR Registration Status
                      {fbrStatusLoading && (
                        <span className="ml-2 text-amber-600 text-xs">(Checking...)</span>
                      )}
                    </label>
                    <select
                      value={formData.fbr_registration_status}
                      onChange={(e) => setFormData({...formData, fbr_registration_status: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {fbrStatusOptions.registration_status_options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Auto-updated when CNIC/NTN is entered
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      FBR Active Status
                      {fbrStatusLoading && (
                        <span className="ml-2 text-amber-600 text-xs">(Checking...)</span>
                      )}
                    </label>
                    <select
                      value={formData.fbr_active_status}
                      onChange={(e) => setFormData({...formData, fbr_active_status: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {fbrStatusOptions.active_status_options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Auto-updated when CNIC/NTN is entered
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingClient(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 shadow-sm hover:shadow-md transition-all"
                >
                  {editingClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading clients...</p>
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
                <p className="text-slate-600">Manage your client database</p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center shadow-sm hover:shadow-md transition-all"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <select
                  value={provinceFilter}
                  onChange={(e) => setProvinceFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Provinces</option>
                  {filterValues.provinces.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
                <select
                  value={registrationTypeFilter}
                  onChange={(e) => setRegistrationTypeFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Types</option>
                  {filterValues.registration_types.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <select
                  value={registrationStatusFilter}
                  onChange={(e) => setRegistrationStatusFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Status</option>
                  <option value="registered">Registered ({filterValues.registration_counts.registered})</option>
                  <option value="unregistered">Unregistered ({filterValues.registration_counts.unregistered})</option>
                </select>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              
              {/* Date Range and Clear Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="To Date"
                />
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center"
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalClients)} of{' '}
                  <span className="font-medium">{totalClients}</span> clients
                </div>
                <nav className="inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-3 py-1 border border-slate-300 bg-white text-xs font-medium text-slate-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* Clients Table */}
          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                <p className="mt-2 text-slate-600">Loading clients...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <User className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p>No clients found</p>
              </div>
            ) : (
              <>
                {selectedClients.length > 0 && (
                  <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex justify-between items-center">
                    <span className="text-sm font-medium text-amber-700">
                      {selectedClients.length} client(s) selected
                    </span>
                    <button
                      onClick={bulkDeactivateClients}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Deactivate Selected
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-slate-300 rounded cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Client</span>
                        {getSortIcon('name')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Contact</span>
                        {getSortIcon('email')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('province')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Location</span>
                        {getSortIcon('province')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <span>FBR Info</span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleSelectClient(client.id)}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-slate-300 rounded cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-8 w-8 text-slate-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-slate-900">
                              {client.name}
                            </div>
                            {client.buyer_business_name && (
                              <div className="text-sm text-slate-500">
                                {client.buyer_business_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">
                          {client.email && (
                            <div className="flex items-center">
                              <span className="text-slate-400 mr-1">📧</span>
                              {client.email}
                            </div>
                          )}
                          {client.phone && (
                            <div className="flex items-center">
                              <span className="text-slate-400 mr-1">📞</span>
                              {client.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">
                          {client.address && (
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 text-slate-400 mr-1" />
                              {client.address}
                            </div>
                          )}
                          {client.province && (
                            <div className="text-sm text-slate-500 mt-1">
                              {client.province}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">
                          {client.cnic && (
                            <div className="text-xs text-slate-500">CNIC: {client.cnic}</div>
                          )}
                          {client.ntn && (
                            <div className="text-xs text-slate-500">NTN: {client.ntn}</div>
                          )}
                          {client.buyer_registration_type && (
                            <div className="text-xs text-slate-500">Type: {client.buyer_registration_type}</div>
                          )}
                          {/* FBR Status Information */}
                          <div className="mt-1 space-y-1">
                            {client.fbr_registration_status && (
                              <div className="flex items-center">
                                <span className="text-xs text-slate-500 mr-2">Reg:</span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  client.fbr_registration_status === 'registered' 
                                    ? 'bg-green-100 text-green-800' 
                                    : client.fbr_registration_status === 'unregistered'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-slate-100 text-slate-800'
                                }`}>
                                  {client.fbr_registration_status}
                                </span>
                              </div>
                            )}
                            {client.fbr_active_status && (
                              <div className="flex items-center">
                                <span className="text-xs text-slate-500 mr-2">Active:</span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  client.fbr_active_status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : client.fbr_active_status === 'inactive'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-slate-100 text-slate-800'
                                }`}>
                                  {client.fbr_active_status}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(client)}
                            className="text-amber-600 hover:text-amber-800"
                            title="Edit Client"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Client"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-slate-700">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalClients)} of{' '}
                      <span className="font-medium">{totalClients}</span> clients
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-700">Items per page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-slate-300 bg-white text-sm font-medium text-slate-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50">
        {renderBody()}
        {renderClientModal()}
      </div>
    </Navigation>
  );
};

export default Clients; 